import React, { useId } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";
import { Template, TemplateType } from "../../types";
import { useTranslation } from "react-i18next";
import { Preview } from "@mui/icons-material";

export interface Props {
  templates: Template[];
  selected?: Template;
  onChange: (template: Template) => void;
  templateType?: TemplateType;
}

export const TemplateSelector = ({
  templates,
  selected,
  onChange,
  templateType,
}: Props) => {
  const [t] = useTranslation();
  const id = useId();

  // Filter templates by type if templateType is provided
  const filteredTemplates = templateType
    ? templates.filter((template) => template.templateType === templateType)
    : templates;

  const sortedTemplates = [...filteredTemplates].sort((x, y) =>
    x.name.localeCompare(y.name),
  );

  // Determine the label based on template type
  const getLabel = () => {
    if (templateType === TemplateType.Rubric) {
      return t("templates.rubric");
    }
    return t("templates.template");
  };

  const label = getLabel();
  const yourTemplates = sortedTemplates.filter((template) => template.isMine);
  const institutionTemplates = sortedTemplates.filter(
    (template) =>
      template.isShared &&
      !template.isMine &&
      // !template.isDirectShare &&
      !template.isGlobal,
  );
  const globalTemplates = sortedTemplates.filter(
    (template) =>
      template.isGlobal &&
      !template.isMine &&
      // !template.isDirectShare &&
      !template.isShared,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
      <FormControl size="small" sx={{ flex: 1 }}>
        <InputLabel id={`${id}-template-select-label`}>{label}</InputLabel>
        <Select
          labelId={`${id}-template-select-label`}
          id={`${id}-template-select`}
          value={selected ? selected.id : ""}
          label={label}
          onChange={(e) => {
            const target = sortedTemplates.find(
              (target) => target.id == e.target.value,
            );
            target && onChange(target);
          }}
          sx={{ flexGrow: 1 }}
        >
          {yourTemplates.length > 0 && (
            <ListSubheader>{t("templates.your")}</ListSubheader>
          )}
          {yourTemplates.map((template, index) => (
            <MenuItem key={template.id} value={template.id}>
              {template.name}
            </MenuItem>
          ))}

          {institutionTemplates.length > 0 && (
            <ListSubheader>{t("templates.institution")}</ListSubheader>
          )}
          {institutionTemplates.map((template, index) => (
            <MenuItem key={template.id} value={template.id}>
              {template.name}
            </MenuItem>
          ))}

          {globalTemplates.length > 0 && (
            <ListSubheader>{t("templates.institution")}</ListSubheader>
          )}
          {globalTemplates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {template.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        href={`/preview-template/${selected?.id}`}
        disabled={!selected}
      >
        <Preview />
      </IconButton>
    </Box>
  );
};
